/**
 * Authentication Context
 * Provides global authentication state management and protection for routes
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { authUtils } from '../utils/auth';
import { spaService } from '../services/spaService';

// Create the auth context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState(null);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setAuthError(null);

        // Check if user has stored authentication
        if (authUtils.isAuthenticated()) {
          const memberName = authUtils.getMemberName();
          const memberEmail = authUtils.getMemberEmail();

          // Verify authentication with API
          try {
            const memberInfo = await spaService.getCurrentMember();
            
            if (memberInfo.success && memberInfo.isAuthenticated) {
              // Update user info from API
              setUser({
                name: memberInfo.member.firstName || memberInfo.member.name || memberName,
                email: memberInfo.member.email || memberEmail,
                memberId: memberInfo.member.id,
                member: memberInfo.member
              });
              setIsAuthenticated(true);
            } else {
              // Authentication expired or invalid
              await logout();
            }
          } catch (error) {
            console.warn('Failed to verify authentication:', error);
            // Clear authentication on error
            await logout();
          }
        } else {
          // No authentication found
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('Authentication initialization error:', error);
        setAuthError('Failed to initialize authentication.');
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (username, password, rememberMe = false) => {
    try {
      setIsLoading(true);
      setAuthError(null);

      const result = await spaService.login(username, password, rememberMe);
      
      if (result.success) {
        // Store authentication info
        authUtils.setAuth(result.memberName || 'Member', result.email || username);
        
        // Set user state
        setUser({
          name: result.memberName || 'Member',
          email: result.email || username,
          memberId: result.memberId
        });
        setIsAuthenticated(true);
        
        return { success: true };
      } else {
        setAuthError(result.error || 'Login failed. Please check your credentials.');
        return { success: false, error: result.error };
      }
    } catch (error) {
      let msg = error.message || error;
      console.error('Login error:', msg);
      setAuthError(msg);
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  };

  // Register function
  const register = async (memberData) => {
    try {
      setIsLoading(true);
      setAuthError(null);

      const result = await spaService.register(memberData);
      
      if (result.success) {
        return { success: true, message: 'Registration successful! Please check your email to verify your account.' };
      } else {
        setAuthError(result.error || 'Registration failed. Please try again.');
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMsg = error;
      setAuthError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setIsLoading(true);
      await authUtils.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      setAuthError(null);
      setIsLoading(false);
    }
  };

  // Refresh user data
  const refreshUser = async () => {
    if (!isAuthenticated) return;

    try {
      const memberInfo = await spaService.getCurrentMember();
      
      if (memberInfo.success && memberInfo.isAuthenticated) {
        setUser(prevUser => ({
          ...prevUser,
          name: memberInfo.member.firstName || memberInfo.member.name || prevUser.name,
          email: memberInfo.member.email || prevUser.email,
          member: memberInfo.member
        }));
        setAuthError(null);
      }
    } catch (error) {
      console.warn('Failed to refresh user data:', error);
      setAuthError('Unable to sync user data.');
    }
  };

  // Clear auth error
  const clearAuthError = () => {
    setAuthError(null);
  };

  // Context value
  const value = {
    // State
    isAuthenticated,
    isLoading,
    user,
    authError,
    
    // Actions
    login,
    register,
    logout,
    refreshUser,
    clearAuthError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;